import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayCircle, ImageIcon, ArrowRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const mediaUrl = (p) => `${API}/media/${p}`;

export default function RepairGallery() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="gallery-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,106,33,0.10),transparent_55%)]" />
        <div className="relative max-w-[1300px] mx-auto px-5 sm:px-8 pt-14 md:pt-20 pb-10">
          <motion.p className="overline text-[#F26A21] mb-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            Real jobs, real results
          </motion.p>
          <motion.h1
            className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Repair videos &{" "}
            <span className="text-[#F26A21]">images.</span>
          </motion.h1>
          <p className="text-[#AEBECD] text-lg max-w-xl mt-5 leading-relaxed">
            After every service, our engineers document the work. Browse real
            repairs and installations from ASA homes across Hyderabad.
          </p>

          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-8" data-testid="gallery-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`gallery-filter-${c}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    filter === c
                      ? "bg-[#F26A21] border-[#F26A21] text-white"
                      : "bg-white/5 border-[#33506e] text-[#AEBECD] hover:border-[#F26A21] hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="max-w-[1300px] mx-auto px-5 sm:px-8 py-12">
        {loaded && filtered.length === 0 && (
          <div data-testid="gallery-empty" className="rounded-3xl border border-[#223851] bg-[#122539] p-16 text-center">
            <ImageIcon size={40} className="text-[#6B7E92] mx-auto" />
            <p className="font-heading font-bold text-2xl text-white mt-4">Gallery coming soon</p>
            <p className="text-[#AEBECD] mt-2">We're adding recent repair photos and videos. Check back shortly.</p>
            <Link to="/plans" className="inline-flex items-center gap-2 mt-6 bg-[#F26A21] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#DE5C15] transition-colors">
              Explore plans <ArrowRight size={16} />
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.06}>
              <div
                data-testid={`gallery-item-${item.id}`}
                className="rounded-3xl overflow-hidden border border-[#223851] bg-[#122539] group"
              >
                <div className="relative aspect-[4/3] bg-[#0C1C2E] overflow-hidden">
                  {item.media_type === "video" ? (
                    <video
                      src={mediaUrl(item.storage_path)}
                      controls
                      preload="metadata"
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={mediaUrl(item.storage_path)}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-black/50 backdrop-blur text-white text-xs px-2.5 py-1 rounded-full">
                    {item.media_type === "video" ? <PlayCircle size={13} /> : <ImageIcon size={13} />}
                    {item.media_type === "video" ? "Video" : "Photo"}
                  </span>
                </div>
                <div className="p-5">
                  <p className="overline text-[#F26A21]">{item.category}</p>
                  <h3 className="font-heading font-bold text-lg text-white mt-1">{item.title}</h3>
                  {item.caption && <p className="text-sm text-[#AEBECD] mt-1">{item.caption}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
